import React, { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import getConfig from "next/config";
import { isMobile } from "react-device-detect";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";
import InputMask from 'react-input-mask';

import { hexToRGBA } from "../../../utils/theme";

const { publicRuntimeConfig } = getConfig();

const StyledGroup = styled(Form.Group)`
  position: relative;

  padding-left: 0;
  padding-right: 0;

  ${({ withLabel }) => withLabel && css`
    padding-top: 24px;
  `};
`;

const StyledLabel = styled(Form.Label)`
  position: absolute;
  left: 0;
  top: 0;
  font-family: 'LatoBold';
  font-size: 12px;
  line-height: 24px;
  text-transform: uppercase;
  white-space: nowrap;
  color: #078FF1;

  span {
    font-size: 16px;
    color: #C61F0C;
  }
`;

const StyledControl = styled(Form.Control)`
  width: 100%;
  min-height: 52px;

  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 16px;
  padding-right: 16px;

  font-family: 'LatoRegular';
  font-size: 14px;
  line-height: 24px;
  color: ${hexToRGBA('#1B3858', 0.5)};

  border: 1px solid ${hexToRGBA('#078FF1', 0.1)};
  border-radius: 7px;

  background-color: ${hexToRGBA('#078FF1', 0.1)};

  ${({ isValid }) => isValid && css`
    border: 1px solid #198754 !important;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_success.svg)`} !important;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
    box-shadow: 0 0 0 0.2rem rgb(40 167 69 / 25%) !important;
  `};

  ${({ isInvalid }) => isInvalid && css`
    border: 1px solid #DC3545 !important;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_failure.svg)`} !important;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
    box-shadow: 0 0 0 0.2rem rgb(220 53 69 / 25%) !important;
  `};

  &::placeholder {
    color: ${hexToRGBA('#1B3858', 0.5)};
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)`

`;

function MaskInput({
  id,
  hint,
  label,
  emptyLabel,
  placeholder,
  required,
  pattern,
  mask,
  maxLength,
  defaultInvalidMessage = '',
  validate,
  isValid,
  isInvalid,
  onKeyPress,
  value = '',
  onChange,
}) {
  const handleChange = useCallback((event) => {
    validate && validate(event, { required });
    onChange && onChange(event);
  }, [onChange, validate, required]);

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? "top" : "right-start",
      delay: { show: 0, hide: 0 },
      trigger: ["hover", "focus"],
      overlay: (
        <Tooltip id={`tooltip-${id}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
    };

    if (!hint || isMobile) {
      props.show = false;
    }

    return props;
  }, [id, hint]);

  const showMessage = !(isValid && !isInvalid);

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup controlId={id} withLabel={Boolean(label || emptyLabel)}>
        <StyledLabel>
          {label}
          {!emptyLabel && label && required && (<span>&nbsp;*</span>)}
        </StyledLabel>
        <InputMask
          mask={mask}
          maskChar={null}
          value={value}
          onChange={handleChange}
        >
          {(_) => (
            <StyledControl
              type="phone"
              name={id}
              required={required}
              isValid={isValid}
              isInvalid={isInvalid}
              placeholder={placeholder}
              pattern={pattern}
              maxLength={maxLength}
              onKeyPress={onKeyPress}
              data-hj-allow
            />
          )}
        </InputMask>
        {showMessage && (
          <StyledFeedback type="invalid">
            {defaultInvalidMessage}
          </StyledFeedback>
        )}
      </StyledGroup>
    </OverlayTrigger>
  );
}

export default MaskInput;
